import { useState, useEffect, useCallback } from 'react';
import { api } from './useAuth';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const usePushNotifications = (user) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [loading, setLoading] = useState(false);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        // Check current permission
        const permission = Notification.permission;
        setPermissionStatus(permission);
        
        // Check existing subscription
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          if (existingSubscription) {
            setSubscription(existingSubscription);
            setIsSubscribed(true);
          }
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      throw error;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      
      // Get VAPID public key from server
      const { data: keyData } = await api.get('/push/vapid-public-key');
      const vapidPublicKey = keyData.publicKey;
      
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }
      
      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      const subscriptionJson = pushSubscription.toJSON();
      await api.post('/push/subscribe', {
        subscription: {
          endpoint: subscriptionJson.endpoint,
          keys: {
            p256dh: subscriptionJson.keys.p256dh,
            auth: subscriptionJson.keys.auth
          }
        }
      });
      
      setSubscription(pushSubscription);
      setIsSubscribed(true);
      
      return true;
    } catch (error) {
      console.error('Subscription error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    
    setLoading(true);
    try {
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      // Notify server
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/unsubscribe', {
        subscription: {
          endpoint: subscriptionJson.endpoint,
          keys: {
            p256dh: subscriptionJson.keys.p256dh,
            auth: subscriptionJson.keys.auth
          }
        }
      });
      
      setSubscription(null);
      setIsSubscribed(false);
      
      return true;
    } catch (error) {
      console.error('Unsubscribe error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!isSubscribed) {
      throw new Error('Not subscribed to notifications');
    }
    
    try {
      await api.post('/push/test');
      return true;
    } catch (error) {
      console.error('Test notification error:', error);
      throw error;
    }
  }, [isSubscribed]);

  return {
    isSupported,
    isSubscribed,
    permissionStatus,
    loading,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
};
